/* eslint-disable react/no-unstable-nested-components */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import TableFilters from '@/components/TableFilters/TableFilters';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { deleteDivision, updateCourseDivision } from '@/hooks/mutations/divisionMutations';
import { useGetDivisions } from '@/hooks/queries/useDivisions';
import { EditIcon, TrashCanIcon } from '@/icons/index';
import type { DivisionWithCourses } from '@/lib/types';
import { themeSelectStyles } from '@/utils';

import { ConfirmationWarning } from './ConfirmationWarning';
import DivisionsModal from './DivisionsModal';

const Divisions = () => {
	const [modalData, setModalData] = useState({
		isActive: false,
		isEditing: false,
		division: {} as DivisionWithCourses,
	});
	const { data: divisions, isFetching, isLoading } = useGetDivisions();

	type DivisionTableData = {
		name: string;
		numberOfCourses: number;
		actions: DivisionWithCourses;
		idColumn: string;
	};

	const [filteredData, setFilteredData] = useState<DivisionTableData[]>([]);
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [isConfirming, setIsConfirming] = useState(false);
	const [isValidated, setIsValidated] = useState(true);
	const [newDivision, setNewDivision] = useState({
		id: '',
		name: '',
	});
	const queryClient = useQueryClient();

	const handleSetData = useCallback((queryDivisionData: any) => {
		setFilteredData(queryDivisionData as DivisionTableData[]);
	}, []);

	const { mutate: deleteDivisionMutation } = useMutation({
		mutationFn: deleteDivision,
		onSuccess: (_, deletedValue) => {
			showSuccessToast('Division Deleted');

			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDivisionData = queryClient.getQueryData(['get-all-divisions', schoolId]) as
				| DivisionWithCourses[]
				| undefined;
			if (previousDivisionData) {
				const updatedDivisions = previousDivisionData.filter(
					(division) => division.id !== deletedValue.id
				);
				queryClient.setQueryData(['get-all-divisions', schoolId], updatedDivisions);
			}
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutate: courseDivisionMutation } = useMutation({
		mutationFn: updateCourseDivision as any,
		onSuccess: (courseDivisions: CoursesDivisionEntity[], idObject: any) => {
			const { previousDivision, newDivision } = idObject;
			showSuccessToast('Divisions and Courses Updated');
			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDivisionData = queryClient.getQueryData(['get-all-divisions', schoolId]) as
				| DivisionWithCourses[]
				| undefined;
			// need to clone data in order to not mutate the cache
			if (previousDivisionData) {
				const updatedCache = [
					...previousDivisionData.filter((division) => division.id !== previousDivision.id),
				];
				const idx = updatedCache.findIndex((division) => division.id === newDivision.id);
				if (idx !== -1) {
					updatedCache[idx].courses_divisions = courseDivisions as any;
					queryClient.setQueryData(['get-all-divisions', schoolId], updatedCache);
				}
			}
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const isAssigned = useCallback(
		(division: DivisionWithCourses) =>
			Boolean(division.courses_divisions && division?.courses_divisions?.length >= 1),
		[]
	);

	const removeDivision = (division: DivisionWithCourses | undefined) => {
		if (!division) return;

		if (!isAssigned(division)) {
			deleteDivisionMutation({
				id: division.id,
			});
			return;
		}

		courseDivisionMutation({
			newDivision,
			previousDivision: division,
		});
	};

	const confirmDelete = useCallback(
		(division: DivisionWithCourses) => {
			setIsValidated(!isAssigned(division));
			setModalData({ isActive: false, isEditing: true, division });
			setIsConfirming(true);
		},
		[isAssigned]
	);

	const getOptionList = () => {
		const filteredDivisions =
			divisions?.filter((division) => division.id !== modalData.division.id) ?? [];

		const options = filteredDivisions.map((division) => ({
			value: division.name,
			label: division.name,
			id: division.id,
		}));

		return options;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const abortController = new AbortController();
		return () => {
			abortController.abort();
		};
	}, [divisions]);

	const queryData = useMemo(
		() =>
			divisions?.map((division) => ({
				name: division.name,
				numberOfCourses: division.courses_divisions?.length ?? 0,
				actions: division,
				idColumn: division.id,
			})),
		[divisions]
	);

	useEffect(() => {
		if (queryData) {
			setFilteredData(queryData as DivisionTableData[]);
		}
	}, [queryData]);

	const columns = useMemo<ColumnDef<DivisionTableData>[]>(
		() => [
			{
				accessorKey: 'name',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
				cell: ({ row }) => <div>{row.getValue('name')}</div>,
			},
			{
				accessorKey: 'numberOfCourses',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Assigned Courses" className="w-full" />
				),
				cell: ({ row }) => (
					<div className="flex justify-center">{row.getValue('numberOfCourses')}</div>
				),
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const updateDivisionModal = () => {
						setModalData({
							isActive: true,
							isEditing: true,
							division: row.original.actions,
						});
					};

					return (
						<div className="flex items-center justify-center [&>button:not(:last-child)]:mr-4">
							<EditIcon passedEvent={updateDivisionModal} />
							<TrashCanIcon
								passedEvent={() => {
									confirmDelete(row.original.actions);
								}}
							/>
						</div>
					);
				},
			},
		],
		[confirmDelete]
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	return (
		<>
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={() => removeDivision(modalData.division)}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					message="Are you sure you want to delete the following division?"
					selectedData={modalData.division?.name ?? ''}
					validated={isValidated}
				>
					{isAssigned(modalData.division) && (
						<>
							<ConfirmationWarning division={modalData.division} />
							<Select
								data-testid="filterSelect"
								styles={themeSelectStyles}
								placeholder="Select a new division"
								onChange={(option) => {
									setNewDivision({
										id: `${option?.id}`,
										name: `${option?.label}`,
									});
									setIsValidated(true);
								}}
								isClearable
								options={[...getOptionList()]}
								noOptionsMessage={() => 'No Divisions Found'}
							/>
						</>
					)}
				</ConfirmModal>
			)}
			{modalData.isActive && (
				<DivisionsModal
					modalData={modalData}
					allDivisions={divisions}
					triggerModal={() =>
						setModalData({
							isActive: false,
							isEditing: false,
							division: {} as DivisionWithCourses,
						})
					}
				/>
			)}
			<div className="flex items-center justify-between">
				<Button
					size="small"
					className="self-start mr-auto"
					onClick={() => {
						setModalData({
							isActive: true,
							isEditing: false,
							division: {} as DivisionWithCourses,
						});
					}}
				>
					Add New Division
				</Button>
				{queryData && (
					<TableFilters
						data={queryData}
						handleSetData={handleSetData}
						searchColumn={['name']}
						filteredColumn="name"
						resultQty={filteredData.length}
						dropdown={false}
					/>
				)}
			</div>
			{(isFetching || isLoading) && (
				<div className="flex flex-col pb-5 h-full">
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
				</div>
			)}
			{!isFetching && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 300px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
		</>
	);
};

export default Divisions;
